import React, {useEffect, useState} from 'react';
import {Button, Space, Table, Image, Modal, Form, Input, InputNumber, TreeSelect, Upload, message, Card} from 'antd';
import http from "../framework/http.js";
import {LoadingOutlined, PlusOutlined, DeleteOutlined, MinusCircleOutlined} from '@ant-design/icons';


function CategoryManagement() {
    const {Column} = Table;
    const [categoryMeta, setCategoryMeta] = useState();
    const [categoryTree, setCategoryTree] = useState();
    const [categorySelectionTree, setCategorySelectionTree] = useState();
    const [imageUrl, setImageUrl] = useState();
    const [uploading, setUploading] = useState(false);

    const [form] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfirmLoading, setModalConfirmLoading] = useState();
    const [submitMethod, setSubmitMethod] = useState();
    const [deleteButtonLoading, setDeleteButtonLoading] = useState(new Map());
    const [editButtonLoading, setEditButtonLoading] = useState(new Map());

    useEffect(() => {
        async function loadCategory() {
            const res = await http.get("/category/list");
            if (res.data?.code === 200) {
                setCategoryMeta(res.data.data);
            }
        }

        loadCategory();
    }, []);

    useEffect(() => {
        function buildCategoryTree(categoryMeta, parentId = 0) {
            const category = [];
            const categorySelectionTree = [];
            for (const item of categoryMeta) {
                if (item.parentId === parentId) {
                    let categoryNode = {key: item.id, name: item.name, picture: item.picture, order: item.order};
                    let categorySelectionTreeNode = {title: item.name, value: item.id};
                    let children = buildCategoryTree(categoryMeta, item.id);
                    if (children[0].length > 0) {
                        categoryNode.children = children[0];
                        categorySelectionTreeNode.children = children[1];
                    }
                    category.push(categoryNode);
                    categorySelectionTree.push(categorySelectionTreeNode);
                }
            }
            return [category, categorySelectionTree];
        }

        if (categoryMeta) {
            const trees = buildCategoryTree(categoryMeta);
            setCategoryTree(trees[0]);
            setCategorySelectionTree([{title: "root", value: 0, children: trees[1]}]);
        }
    }, [categoryMeta]);


    const submitForm = async (formData) => {
        if (uploading) {
            message.warning("upload haven't finish, try again later");
        } else {
            setModalConfirmLoading(true);
            // data processing
            formData.picture = imageUrl;
            let parentId = formData.parentId;
            let path;
            while (parentId !== 0) {
                const categoryItem = categoryMeta.find(o => o.id === parentId);
                parentId = categoryItem.parentId;
                if (path) {
                    path = categoryItem.id + "-" + path
                } else {
                    path = categoryItem.id
                }
            }
            if (path) {
                formData.path = "0-" + path;
            } else {
                formData.path = "0";
            }

            if (submitMethod === "insert") {
                await insertCategory(formData)
            } else if (submitMethod === "update") {
                await updateCategory(formData);
            }

            setModalConfirmLoading(false);
        }
    };

    const insertCategory = async (formData) => {
        const res = await http.post("/category", formData);
        if (res.data?.code === 200) {
            formData.id = res.data.data;
            setCategoryMeta([formData, ...categoryMeta]);
            message.success("New record added");
            setModalOpen(false);
            form.resetFields();
            setImageUrl();
        } else if (res.data?.code !== 401) {
            message.error(res.data?.msg || "Error, please try again later");
        }
    };

    const updateCategory = async (formData) => {
        const res = await http.put("/category", formData);
        if (res.data?.code === 200) {
            setCategoryMeta(categoryMeta.map(item => {
                if (item.id === formData.id) {
                    return formData;
                } else {
                    return item;
                }
            }));

            setModalOpen(false);
            form.resetFields();
            setImageUrl();
            message.success("Record updated");
        } else if (res.data?.code !== 401) {
            message.error(res.data?.msg || "Error, please try again later");
        }
    };

    const onDeleteClick = async (id) => {
        let newDeleteButtonLoading = new Map(deleteButtonLoading);
        newDeleteButtonLoading.set(id, true);
        setDeleteButtonLoading(newDeleteButtonLoading);

        const res = await http.delete("/category/" + id);
        if (res.data?.code === 200) {
            setCategoryMeta(categoryMeta.filter(o => o.id !== id));
            message.success("Record deleted!");
        } else if (res.data?.code !== 401) {
            message.error(res.data?.msg || "Error, please try again later");
        }

        newDeleteButtonLoading = new Map(newDeleteButtonLoading);
        newDeleteButtonLoading.delete(id);
        setDeleteButtonLoading(newDeleteButtonLoading);
    };

    const onEditClick = async (id) => {
        let newEditButtonLoading = new Map(editButtonLoading);
        newEditButtonLoading.set(id, true);
        setEditButtonLoading(newEditButtonLoading);

        const categoryFind = categoryMeta.find(o => o.id === id);

        setImageUrl(categoryFind.picture);
        form.setFieldsValue({
            id: categoryFind.id,
            name: categoryFind.name,
            parentId: categoryFind.parentId,
            picture: [{
                status: 'done',
                name: categoryFind.picture,
                url: http.getUri() + categoryFind.picture
            }],
            order: categoryFind.order
        });

        await setSubmitMethod("update");
        setModalOpen(true);

        newEditButtonLoading = new Map(newEditButtonLoading);
        newEditButtonLoading.delete(id);
        setEditButtonLoading(newEditButtonLoading);
    };

    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isJpgOrPng) {
            message.error('Only JPG/PNG file is accepted');
        } else {
            if (!isLt2M) {
                message.error('Image must smaller than 2MB');
            }
        }
        return isJpgOrPng && isLt2M;
    };

    const handleUploadProcess = (info) => {
        if (info.file.status === 'uploading') {
            setUploading(true);
            return;
        }
        if (info.file.status === 'done') {
            setUploading(false);
            if (info.file.response.data && info.file.response.code === 200) {
                setImageUrl(info.file.response.data.path);
            }
        }
    };

    return (
        <div>
            <Space style={{marginLeft: 10, marginBottom: 10}}>
                <Button type="primary" onClick={async () => {
                    await setSubmitMethod("insert");
                    setModalOpen(true);
                }}>Add</Button>
            </Space>
            <Modal maskClosable={false} open={modalOpen} onOk={() => {
                form.submit()
            }} confirmLoading={modalConfirmLoading} onCancel={() => {
                setModalOpen(false);
                form.resetFields();
                setImageUrl();
            }}>
                <Form form={form} style={{marginTop: 30}} onFinish={submitForm}>
                    <Form.Item hidden={true} label="Id" name="id">
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: 'Category Name can not be empty',
                            },
                        ]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        label="Parent Node"
                        name="parentId"
                        initialValue={0}
                        rules={[
                            {
                                required: true,
                                message: 'Parent node must be selected',
                            },
                        ]}
                    >
                        <TreeSelect treeLine={true} showSearch treeNodeFilterProp="title" allowClear
                                    placeholder="Please select" treeData={categorySelectionTree}/>
                    </Form.Item>
                    <Form.Item
                        label="Picture"
                        name="picture"
                        valuePropName={"fileList"}
                        getValueFromEvent={(e) => {
                            if (Array.isArray(e)) {
                                return e;
                            }
                            return e?.fileList;
                        }}
                        initialValue={null}
                    >
                        <Upload
                            listType="picture-card"
                            accept=".jpg, .jpeg, .png"
                            showUploadList={false}
                            maxCount={1}
                            action={http.getUri() + "upload"}
                            name="pictureFile" // parameter name
                            data={{foldName: "category"}}
                            headers={{token: window.localStorage.getItem("token")}}
                            beforeUpload={beforeUpload}
                            onChange={handleUploadProcess}
                        >
                            {imageUrl && !uploading ? (
                                <div style={{position: "relative", width: '100%', height: "100%"}}>
                                    <img src={http.getUri() + imageUrl}
                                         style={{width: '100%', height: "100%", borderRadius: "8%"}} alt=""/>
                                    <DeleteOutlined
                                        style={{
                                            position: "absolute",
                                            left: "110%",
                                            top: "50%",
                                            transform: "translate(0%,-50%)"
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setImageUrl();
                                        }}
                                    />
                                </div>
                            ) : (
                                <div>
                                    {uploading ? <LoadingOutlined/> : <PlusOutlined/>}
                                    <div style={{marginTop: 8,}}>Upload</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>
                    <Form.Item
                        label="Order"
                        name="order"
                        initialValue={0}
                        rules={[
                            {
                                required: true,
                                message: 'Order field can not be empty',
                            }
                        ]}
                    >
                        <InputNumber style={{width: '100%',}} min={0} precision={0}/>
                    </Form.Item>
                </Form>
                {/*<p>{modalText}</p>*/}
            </Modal>
            {/*<Table columns={columns} dataSource={data}>*/}
            <Table dataSource={categoryTree} pagination={{hideOnSinglePage: true, pageSize: 30}}>
                <Column title="" key="null"/>
                {/*<Column title="ID" dataIndex="key"/>*/}
                <Column title="Name" dataIndex="name"/>
                <Column title="Picture" dataIndex="picture"
                        render={(src) => (src ? <Image width={50} height={50} src={http.getUri() + src}/> :
                            <Image width={50} height={50} src={http.getUri() + "images/default.png"}/>)}/>
                <Column title="Order" dataIndex="order"/>
                <Column title="Operation" width="15%"
                        render={(_, record, index) => (
                            <Space>
                                <Button danger
                                        type="primary"
                                        loading={deleteButtonLoading.get(record.key)}
                                        onClick={() => {
                                            onDeleteClick(record.key);
                                        }}
                                >Delete</Button>
                                <Button type="primary"
                                        loading={editButtonLoading.get(record.key)}
                                        onClick={() => {
                                            onEditClick(record.key);
                                        }}
                                >Edit</Button>
                            </Space>
                        )}
                />
            </Table>
        </div>
    );
}

export default CategoryManagement;
